import { create } from "zustand";
import type { Dialog } from "@/types/dialog";
import { getLanguageConfig } from "@/lib/language-config";

interface DialogToggles {
  romanization: boolean;
  translation: boolean;
  nastaliq: boolean;
  vocalization: boolean;
  transcription: boolean;
}

type RepeatMode = "off" | "line" | "all";

interface AudioState {
  isPlaying: boolean;
  isPlayingAll: boolean;
  currentLineIndex: number;
  playbackSpeed: number;
  repeatMode: RepeatMode;
  loadingLine: number | null;
}

interface RolePlayState {
  isOpen: boolean;
  isActive: boolean;
  isCompleted: boolean;
  selectedRole: string | null;
  helperTextLevel: number;
  useTranscriptionMode: boolean;
  currentIndex: number;
  cheatAttempts: number;
  lastTapTime: number;
}

interface DialogState {
  dialog: Dialog | null;
  loading: boolean;
  error: string | null;
  toggles: DialogToggles;
  activeLine: number | null;
  textSizeLevel: number;
  audio: AudioState;
  rolePlay: RolePlayState;

  setDialog: (dialog: Dialog) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  toggle: (key: keyof DialogToggles) => void;
  setActiveLine: (index: number | null) => void;
  setTextSizeLevel: (level: number) => void;
  setAudio: (partial: Partial<AudioState>) => void;
  setRolePlay: (partial: Partial<RolePlayState>) => void;
  reset: () => void;
}

function defaultToggles(languageCode?: string): DialogToggles {
  if (!languageCode) {
    return { romanization: true, translation: false, nastaliq: false, vocalization: false, transcription: false };
  }
  const config = getLanguageConfig(languageCode);
  return {
    romanization: config.hasRomanization,
    translation: false,
    nastaliq: false,
    vocalization: config.hasVocalization,
    transcription: false,
  };
}

const defaultAudio: AudioState = {
  isPlaying: false,
  isPlayingAll: false,
  currentLineIndex: 0,
  playbackSpeed: 1.0,
  repeatMode: "off",
  loadingLine: null,
};

const defaultRolePlay: RolePlayState = {
  isOpen: false,
  isActive: false,
  isCompleted: false,
  selectedRole: null,
  helperTextLevel: 100,
  useTranscriptionMode: false,
  currentIndex: 0,
  cheatAttempts: 0,
  lastTapTime: 0,
};

export const useDialogStore = create<DialogState>((set) => ({
  dialog: null,
  loading: false,
  error: null,
  toggles: defaultToggles(),
  activeLine: null,
  textSizeLevel: 0,
  audio: defaultAudio,
  rolePlay: defaultRolePlay,

  setDialog: (dialog) => {
    set({
      dialog,
      loading: false,
      error: null,
      toggles: defaultToggles(dialog.language_code),
      activeLine: null,
      audio: defaultAudio,
      rolePlay: defaultRolePlay,
    });
  },

  setLoading: (loading) => {
    set({ loading });
  },

  setError: (error) => {
    set({ error, loading: false });
  },

  toggle: (key) => {
    set((state) => ({
      toggles: { ...state.toggles, [key]: !state.toggles[key] },
    }));
  },

  setActiveLine: (index) => {
    set({ activeLine: index });
  },

  setTextSizeLevel: (level) => {
    set({ textSizeLevel: Math.max(-2, Math.min(4, level)) });
  },

  setAudio: (partial) => {
    set((state) => ({
      audio: { ...state.audio, ...partial },
    }));
  },

  setRolePlay: (partial) => {
    set((state) => ({
      rolePlay: { ...state.rolePlay, ...partial },
    }));
  },

  reset: () => {
    set({
      dialog: null,
      loading: false,
      error: null,
      toggles: defaultToggles(),
      activeLine: null,
      textSizeLevel: 0,
      audio: defaultAudio,
      rolePlay: defaultRolePlay,
    });
  },
}));
